import {
  SimpleAccordion,
  Input,
  TextArea,
  Button,
  CustomLabel,
  FormTabSwitcher,
  Toggle,
  EasyFilterView,
  UploadButton,
  CircleButton,
  EasyModal,
} from '@ui';
import React, { FC, ReactNode, useState } from 'react';

import { v4 } from 'uuid';

import { Controller, useFieldArray, useForm } from 'react-hook-form';

import {
  GPTGroupMetricListObjectType,
  GPTGroupObjectType,
  IndividualGptMetricType,
} from '@app/interfaces/gpt-metric.type';

import { useOutletContext, useParams } from 'react-router';

import {
  Filter,
  FilterQmOperatorsType,
  FilterUcid,
  Range,
} from '@app/components/ui/easy-filter/types';

import { MetricListArrType } from '@app/interfaces/pages-types/anatylics-metric.type';

import {
  useCreateProjectMetricGroupForGptMutation,
  useDeleteProjectMetricForGptMutation,
  useEditProjectMetricGroupForGptMutation,
  useGetGptGroupsWithMetricsQuery,
} from '@app/store/api/gpt-metric.api';

import {
  MetricTemplatesGroupType,
  MetricTemplatesType,
} from '@app/interfaces/slices-types/metric-templates.type';

import useTranslation from '@app/hooks/use-translation';

import { useCreateMetricTemplateMutation } from '@app/store/api/metric-template.api';

import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';

import { AllStatusProject } from '@app/interfaces/analytics';

import { GPTGroupMetricListChatObject } from '@app/interfaces/gpt-metric-chats.type';

import {
  useCreateProjectMetricGroupForChatGptMutation,
  useDeleteProjectMetricForChatGptMutation,
  useEditProjectMetricGroupForChatGptMutation,
  useGetChatGptGroupsWithMetricsQuery,
} from '@app/store/api/chat-api/gpt-metric-chat.api';

import { usePermissionCheck } from '@app/hooks/use-permission';

import { userPermissions } from '@app/utils/user-permissions';

import { toast } from 'react-toastify';

import { AddToTemplateModal } from '../../../add-to-template-modal/add-to-template-modal';

import GridWrapper from './grid-wrapper';

type FormType = IndividualGptMetricType & {
  additionalId: string;
  validationValue: string;
  validationResponseToggle: boolean;
  metricResponseType: 'num' | 'str' | 'text';
};

const emptyFilter: Filter = {
  filter: [],
};

type AiMetricIndividualMetricPropsType = {
  gptModalData: GPTGroupObjectType | undefined;
  voiceMetricList: MetricListArrType[] | undefined;
  currentTemplate: 'chat' | 'voice';
  gpt_metrics:
    | GPTGroupMetricListObjectType['gpt_groups']
    | GPTGroupMetricListChatObject['gpt_groups'];
  metricTemplates: MetricTemplatesGroupType[] | undefined;
  currentMetricsDataFromGroup:
    | ({
        metric_id: string;
      } & IndividualGptMetricType)[]
    | undefined;
};
export const AiMetricIndividualMetric: FC<AiMetricIndividualMetricPropsType> = (props) => {
  const { id } = useParams();
  const { t } = useTranslation('pages.metricTemplates');
  const { status } = useOutletContext<AnalyticPageOutlet>();

  const project_id = id || '';
  const {
    gptModalData,
    voiceMetricList,
    currentMetricsDataFromGroup,
    gpt_metrics,
    metricTemplates,
    currentTemplate,
  } = props;
  // api
  const gptGptGroupMetricsAccess = usePermissionCheck({
    permissions: 'action',
    tag: userPermissions.action.actionGetGPTGroupMetrics,
  });
  const { data: gptGroupsListVoice } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !!gptGptGroupMetricsAccess || !project_id || currentTemplate === 'chat' },
  );
  const { data: gptGroupsListChat } = useGetChatGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !!gptGptGroupMetricsAccess || !project_id || currentTemplate === 'voice' },
  );
  const gptGroupsList =
    { chat: gptGroupsListChat, voice: gptGroupsListVoice }?.[currentTemplate] || undefined;
  const [createMetricTemplate] = useCreateMetricTemplateMutation();

  const [createMetricVoice] = useCreateProjectMetricGroupForGptMutation();
  const [createMetricChat] = useCreateProjectMetricGroupForChatGptMutation();
  const createMetric = { chat: createMetricChat, voice: createMetricVoice }[currentTemplate];

  const [deleteMetricVoice] = useDeleteProjectMetricForGptMutation();
  const [deleteMetricChat] = useDeleteProjectMetricForChatGptMutation();
  const deleteMetric = { chat: deleteMetricChat, voice: deleteMetricVoice }[currentTemplate];

  const [editMetricVoice] = useEditProjectMetricGroupForGptMutation();
  const [editMetricChat] = useEditProjectMetricGroupForChatGptMutation();
  const editMetric = { chat: editMetricChat, voice: editMetricVoice }[currentTemplate];

  const formDefaultData = currentMetricsDataFromGroup?.map<FormType>((metric) => ({
    ...metric,
    validationResponseToggle: !!metric.settings.validation.value?.length,
    validationValue:
      metric.settings.validation.type === 'none'
        ? ''
        : metric.settings.validation?.value?.join(
            `${metric.settings.validation.type === 'array' ? ',' : '-'}`,
          ) || '',
    additionalId: metric.metric_id,
    dnt_display: metric.dnt_display || false,
    metricResponseType:
      metric.result_value_type === 'num'
        ? 'num'
        : metric.settings.str_validation === 'text'
        ? 'str'
        : 'text',
  }));
  // api
  const [newIndividualMetricId, setNewIndividualMetricId] = useState<string>();
  const {
    control,
    register,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<{
    manual_metrics: FormType[];
  }>({ defaultValues: { manual_metrics: formDefaultData } });

  const { append, fields, remove } = useFieldArray({ control, name: 'manual_metrics' });
  // ACCORDION
  const [accordionOpenedIds, setIds] = useState<string[]>(['INDIVIDUAL_GPT_METRICS']);
  function handleClickAccordion(id: string) {
    setIds((prev) => {
      return prev.includes(id) ? prev.filter((ids) => ids !== id) : [...prev, id];
    });
  }
  // ACCORDION
  function appendManualMetricBlock() {
    if (!gptModalData?.gpt_group_id) return;
    const newField: FormType = {
      additionalId: v4(),
      name: '',
      validationResponseToggle: false,
      metricResponseType: 'num' as const,
      result_value_type: 'num',
      validationValue: '',
      dnt_display: false,
      description: '',
      type: 'gpt',
      export: { export_result: false },
      settings: {
        gpt_group: gptModalData?.gpt_group_id,
        prompt: '',
        validation: { type: 'none' },
        condition: emptyFilter,
      },
    };
    append(newField);
    setNewIndividualMetricId(newField.additionalId);
    setIds((prev) => {
      if (!newField.additionalId) return prev;
      return [...prev, newField.additionalId];
    });
  }

  function saveIndividualMetricToCollection(mode: 'create' | 'edit', id?: string) {
    if (!id) return;
    const target = { ...watch().manual_metrics.find((i) => i.additionalId === id) };
    if (!target) return;
    const data: IndividualGptMetricType = {
      type: 'gpt',
      description: target?.description || '',
      name: target?.name || '',
      result_value_type: target.result_value_type || 'str',
      dnt_display: target.dnt_display || false,
      export: target.export,
      settings: {
        condition: target.settings?.condition || emptyFilter,
        gpt_group: target.settings?.gpt_group || '',
        prompt: target.settings?.prompt || '',
        str_validation:
          target.result_value_type === 'num'
            ? undefined
            : target.settings?.str_validation || 'text',
        validation: !target.validationResponseToggle
          ? { type: 'none' }
          : validationFormatter(
              target.validationValue,
              target.settings?.str_validation,
              target.result_value_type,
            ),
      },
    };

    trigger('manual_metrics').then((valid) => {
      if (!valid) return;

      mode === 'create'
        ? createMetric({ params: { project_id }, body: data })
            .unwrap()
            .then((response) => {
              const rebuildData = watch().manual_metrics.map((i) => {
                if (i.additionalId === newIndividualMetricId && response.metric_id)
                  return { ...i, additionalId: response.metric_id };
                return i;
              });
              setValue('manual_metrics', rebuildData);
              setNewIndividualMetricId(undefined);
            })
        : target.additionalId &&
          editMetric({ params: { project_id, metric_id: target.additionalId }, body: data });
    });
  }

  function validationFormatter(
    value?: string,
    str_validation?: 'text' | 'longtext',
    result_value_type?: 'str' | 'num',
  ): FormType['settings']['validation'] {
    const defaultState = { type: 'none' as const };
    if (!value || !result_value_type) return defaultState;

    switch (result_value_type) {
      case 'num':
        if (/-/g.test(value)) {
          return { type: 'range', value: value.split('-').map((n) => Number(n.trim())) };
        } else if (/,/g.test(value)) {
          return { type: 'array', value: value.split(',').map((n) => Number(n.trim())) };
        } else {
          return defaultState;
        }
        break;
      case 'str':
        if (str_validation === 'longtext') {
          return defaultState;
        } else {
          if (/,/g.test(value)) {
            return { type: 'array', value: value.split(',').map((n) => n.trim()) };
          } else {
            return defaultState;
          }
        }

        break;

      default:
        return defaultState;
        break;
    }
  }

  function removeManualMetricBlock(index: number) {
    remove(index);
    setNewIndividualMetricId(undefined);
  }

  // 1
  function handleChangeMetricResponseType(value: 'num' | 'str' | 'text', index: number) {
    setValue(`manual_metrics.${index}.metricResponseType`, value);
    switch (value) {
      case 'num':
        setValue(`manual_metrics.${index}.result_value_type`, 'num');
        setValue(`manual_metrics.${index}.settings.str_validation`, undefined);
        break;
      case 'str':
        setValue(`manual_metrics.${index}.result_value_type`, 'str');
        setValue(`manual_metrics.${index}.settings.str_validation`, 'text');
        break;
      case 'text':
        setValue(`manual_metrics.${index}.result_value_type`, 'str');
        setValue(`manual_metrics.${index}.settings.str_validation`, 'longtext');
        setValue(`manual_metrics.${index}.settings.validation`, {
          type: 'none',
        });
        setValue(`manual_metrics.${index}.validationResponseToggle`, false);
        setValue(`manual_metrics.${index}.validationValue`, '');
        break;

      default:
        break;
    }
  }
  // 1
  // FILTER
  function handleFilterLiveReportRecords(
    newFilter: Filter & Range & Partial<FilterQmOperatorsType> & FilterUcid,
    index: number,
  ) {
    setValue(`manual_metrics.${index}.settings.condition`, {
      filter: newFilter.filter,
      ucid: newFilter.ucid,
    });
  }
  function getFilteringKeys() {
    const gptMetrics = gpt_metrics.flatMap((group) => group.gpt_metrics) || [];

    return [
      ...(voiceMetricList || []).filter((metric) => metric.type !== 'calculated'),
      ...gptMetrics,
    ]?.map((metric) => ({
      id: metric.metric_id,
      title: metric.name,
      type_value: metric.result_value_type,
    }));
  }
  // FILTER

  function handleRemoveMetric(id: string, index: number) {
    if (!id) return;
    deleteMetric({ metric_id: id, project_id })
      .unwrap()
      .then(() => removeManualMetricBlock(index));
  }

  const descriptionsByMetricType: Record<'str' | 'text' | 'num', string> = {
    num: 'Целое число без дробей',
    str: 'Максимально 256 символов (включая пробелы)',
    text: 'Максимально 4000 символов (включая пробелы)',
  };

  // TEMPLATES
  const [activeAccordionItem, setActiveAccordionItem] = useState<string | undefined>(undefined);
  const [metricIdToFill, setMetricIdToFill] = useState<string>();

  const clickAccordionHeaderBtn = (id: string) => {
    if (id === activeAccordionItem) {
      if (id === '') {
        setActiveAccordionItem(undefined);
      } else {
        setActiveAccordionItem('');
      }
    } else {
      setActiveAccordionItem(id);
    }
  };

  function handleFillMetricFromTemplate(id: string) {
    setMetricIdToFill(id);
  }
  function handleFillMetric(template: MetricTemplatesType) {
    try {
      setValue(
        'manual_metrics',
        watch('manual_metrics').map((metric) => {
          if (metric.additionalId === metricIdToFill) {
            const formatted: FormType = {
              ...metric,
              name: template.name || metric.name,
              description: template.description || metric.description,
              result_value_type: (template.result_value_type as 'num') || metric.result_value_type,
              settings: {
                str_validation: template.settings.str_validation || metric.settings.str_validation,
                gpt_group: metric.settings.gpt_group,
                prompt: template.settings.prompt || metric.settings.prompt,
                condition: metric.settings.condition,
                validation: template.settings.validation || metric.settings.validation,
              },
              metricResponseType:
                template.result_value_type === 'num'
                  ? 'num'
                  : template.settings.str_validation === 'text'
                  ? 'str'
                  : 'text',
              validationResponseToggle: !!template.settings?.validation?.value?.length,
              validationValue:
                template.settings?.validation?.type === 'none'
                  ? ''
                  : template.settings.validation?.value?.join(
                      `${template.settings.validation.type === 'array' ? ',' : '-'}`,
                    ) || '',
            };
            return formatted;
          }
          return metric;
        }),
      );
    } finally {
      setMetricIdToFill(undefined);
    }
  }

  const [metricToTemplate, setMetricToTemplate] = useState<
    IndividualGptMetricType & {
      validationResponseToggle: boolean;
      validationValue: string;
    }
  >();
  const getDefaultTemplate = metricTemplates?.find((e) => e.is_default)?.metric_templates_group_id;
  const createMetricTemplateApplyBtn = () => {
    if (!getDefaultTemplate) return;
    const finalData: IndividualGptMetricType = {
      type: 'gpt',
      description: metricToTemplate?.description || '',
      name: metricToTemplate?.name || '',
      result_value_type: metricToTemplate?.result_value_type || 'str',
      // dnt_display: metricToTemplate?.dnt_display || false,
      settings: {
        condition: metricToTemplate?.settings?.condition || emptyFilter,
        gpt_group: undefined,
        prompt: metricToTemplate?.settings?.prompt || '',
        str_validation:
          metricToTemplate?.result_value_type === 'num'
            ? undefined
            : metricToTemplate?.settings?.str_validation || 'text',
        validation: !metricToTemplate?.validationResponseToggle
          ? { type: 'none' }
          : validationFormatter(
              metricToTemplate?.validationValue,
              metricToTemplate?.settings?.str_validation,
              metricToTemplate?.result_value_type,
            ),
      },
    };

    createMetricTemplate({
      params: { metric_templates_group_id: getDefaultTemplate },
      body: finalData,
    }).then(() => {
      setMetricToTemplate(undefined);
    });
  };

  // TEMPLATES

  // Metric transfer
  const [metricToTransfer, setTransferMetric] = useState<FormType>();
  const [gptGroupIdForTransfer, setGroupId] = useState<string>();

  function onCloseTransferMetricModal() {
    setTransferMetric(undefined);
  }
  function onCloseApproveTransferModal() {
    setGroupId(undefined);
  }
  function handleChooseGroupToTransfer(gpt_group_id: string) {
    setGroupId(gpt_group_id);
  }
  function handleTransferMetric() {
    if (!gptGroupIdForTransfer) return;
    const metricIndex = watch('manual_metrics').findIndex(
      (metric) => metric.additionalId === metricToTransfer?.additionalId,
    );
    const formattedData = {
      ...metricToTransfer,
      settings: { ...metricToTransfer?.settings, gpt_group: gptGroupIdForTransfer },
    } as IndividualGptMetricType;

    delete formattedData['additionalId'];
    delete formattedData['validationValue'];
    delete formattedData['validationResponseToggle'];
    delete formattedData['metricResponseType'];
    metricToTransfer?.additionalId &&
      editMetric({
        params: { project_id, metric_id: metricToTransfer.additionalId },
        body: formattedData,
      })
        .unwrap()
        .then(() => {
          removeManualMetricBlock(metricIndex);
          toast.success('Метрика успешно перенесена');
        })
        .catch(() => toast.error('Ошибка'))
        .finally(() => {
          onCloseApproveTransferModal();
          onCloseTransferMetricModal();
        });
  }
  // Metric transfer

  return (
    <>
      <div className="flex flex-col gap-[18px] py-[32px]">
        <SimpleAccordion
          label={'Индивидуальные GPT-метрики'}
          opened={accordionOpenedIds.includes('INDIVIDUAL_GPT_METRICS')}
          id="INDIVIDUAL_GPT_METRICS"
          onChange={handleClickAccordion}
        >
          <div className="flex flex-col gap-[18px]">
            {fields.map((field, index) => {
              const isNew = newIndividualMetricId === field.additionalId;
              const isLast = fields.length - 1 === index;
              const isOpened =
                field.additionalId && accordionOpenedIds.includes(field.additionalId);
              return (
                <IndividualMetricItemWrapper
                  accordion={{
                    accordionOpenedIds: accordionOpenedIds,
                    handleClickAccordion: handleClickAccordion,
                    id: field.additionalId || '',
                  }}
                  handleFillMetricFromTemplate={() =>
                    handleFillMetricFromTemplate(field.additionalId)
                  }
                  onCancel={() => removeManualMetricBlock(index)}
                  key={field.id}
                  label={
                    <div className="grid grid-rows-1 grid-cols-[250px_1fr] items-center w-full  gap-x-[12px] text-[16px] font-normal leading-[187%] tracking-[-0.03em]">
                      <div
                        className={`whitespace-nowrap ${
                          isOpened ? 'w-full' : 'max-w-[250px] min-w-[250px]'
                        } truncate flex items-center`}
                      >
                        <span className="truncate ">{watch(`manual_metrics.${index}.name`)}</span>
                      </div>
                      {!isOpened ? (
                        <div className="p-[4px_8px]  rounded-[12px] bg-defaultBg flex w-full items-center truncate">
                          {'“'}
                          <span className=" truncate ">
                            {watch(`manual_metrics.${index}.settings.prompt`)}
                          </span>
                          {'”'}
                        </div>
                      ) : (
                        <div className="justify-self-end flex items-center danger">
                          <CircleButton
                            className="text-action"
                            icon="RollbackIcon"
                            hintTitle="Перенести метрику"
                            onClick={() => setTransferMetric(watch(`manual_metrics.${index}`))}
                            size={16.5}
                          />
                          <CircleButton
                            className="text-action"
                            icon="FolderPlusIcon"
                            hintTitle="Сохранить как шаблон"
                            onClick={() => setMetricToTemplate(watch(`manual_metrics.${index}`))}
                            size={18.5}
                          />
                          <CircleButton
                            className="text-action"
                            hintTitle="Загрузить из шаблона"
                            icon="FolderIcon"
                            onClick={() => handleFillMetricFromTemplate(field.additionalId)}
                            size={17}
                          />
                          <Button
                            className="h-fit "
                            variant="danger"
                            fill="linked"
                            label="Удалить"
                            onClick={() => handleRemoveMetric(field.additionalId, index)}
                            disabled={status?.project_status === AllStatusProject.ACTIVE}
                          />
                        </div>
                      )}
                    </div>
                  }
                  isNew={isNew}
                >
                  <GridWrapper>
                    {/* GRID START */}
                    <div className="flex flex-col gap-[18px]">
                      <div>
                        <Input
                          {...register(`manual_metrics.${index}.name`, {
                            required: { message: ' ', value: true },
                          })}
                          placeholder="Введите текст"
                          label="Название метрики"
                          error={errors?.manual_metrics?.[index]?.name?.message}
                        />
                      </div>
                      <Controller
                        control={control}
                        name={`manual_metrics.${index}.settings.prompt`}
                        render={({ field }) => (
                          <div>
                            <TextArea
                              defaultValue={field.value}
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value);
                              }}
                              height={183}
                              popupModal
                              placeholder="Введите ваш промпт"
                              label="Индивидуальный промпт"
                              error={errors?.manual_metrics?.[index]?.settings?.prompt?.message}
                            />
                          </div>
                        )}
                        rules={{ required: { message: ' ', value: true } }}
                      />
                      {/* {!isNotAdmin && !metricTemplate && ( */}
                      <>
                        <div className="flex items-center gap-[8px]">
                          <Toggle
                            checked={watch(`manual_metrics.${index}.export.export_result`)}
                            onChecked={(val) =>
                              setValue(`manual_metrics.${index}.export.export_result`, val)
                            }
                          />
                          <CustomLabel label={t('export_result')} />
                        </div>
                        <div className="w-[280px] ">
                          <Input
                            {...register(`manual_metrics.${index}.export.export_id`, {
                              required: {
                                value: !!watch(`manual_metrics.${index}.export.export_result`),
                                message: ' ',
                              },
                            })}
                            label={t('export_id')}
                            error={errors.manual_metrics?.[index]?.export?.export_id?.message}
                            disabled={!watch(`manual_metrics.${index}.export.export_result`)}
                          />
                        </div>
                      </>
                      {/* )} */}

                      {isLast && (
                        <>
                          {isNew && (
                            <div className="flex items-center justify-center">
                              <Button
                                onClick={() =>
                                  saveIndividualMetricToCollection('create', field.additionalId)
                                }
                                label="Сохранить метрику"
                                fill="linked"
                                className="h-fit"
                              />
                            </div>
                          )}
                        </>
                      )}
                      {!isNew && (
                        <div className="flex items-center justify-center pb-4">
                          <Button
                            onClick={() =>
                              saveIndividualMetricToCollection('edit', field.additionalId)
                            }
                            label="Сохранить изменения"
                            fill="linked"
                            className="h-fit"
                            disabled={status?.project_status === AllStatusProject.ACTIVE}
                          />
                        </div>
                      )}
                    </div>
                    {/* GRID END */}
                    {/* GRID START */}
                    <div className="flex flex-col gap-[18px]">
                      <div className="flex flex-col gap-[6px]">
                        <CustomLabel label="Формат ответа" />
                        <div className="flex flex-col gap-[8px]">
                          <FormTabSwitcher
                            valuesList={[
                              { title: 'Число', value: 'num' },
                              { title: 'Строка', value: 'str' },
                              { title: 'Текст', value: 'text' },
                            ]}
                            isActive={watch(`manual_metrics.${index}.metricResponseType`) || ''}
                            onChange={(value) =>
                              handleChangeMetricResponseType(value as 'num', index)
                            }
                          />
                          <span className="text-[13px] font-normal leading-[109%] tracking-[-0.03em] text-3color opacity-50">
                            {watch(`manual_metrics.${index}.settings.str_validation`) === 'longtext'
                              ? descriptionsByMetricType['text']
                              : descriptionsByMetricType[
                                  watch(`manual_metrics.${index}.result_value_type`)
                                ]}
                          </span>
                          <div>
                            <Toggle
                              checked={watch(`manual_metrics.${index}.validationResponseToggle`)}
                              disabled={
                                watch(`manual_metrics.${index}.metricResponseType`) === 'text'
                              }
                              onChecked={(val) =>
                                setValue(`manual_metrics.${index}.validationResponseToggle`, val)
                              }
                              label="Включить валидацию ответа"
                            />
                          </div>
                          <div>
                            <Input
                              {...register(`manual_metrics.${index}.validationValue`)}
                              disabled={!watch(`manual_metrics.${index}.validationResponseToggle`)}
                              placeholder="Введите ответ для валидации"
                            />
                          </div>
                          <div>
                            <Toggle
                              checked={watch(`manual_metrics.${index}.dnt_display`)}
                              onChecked={(val) =>
                                setValue(`manual_metrics.${index}.dnt_display`, val)
                              }
                              label="Не отображать в просмотре записи"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-[6px]">
                        <CustomLabel label="Условия запуска" />
                        <div>
                          <EasyFilterView
                            data={{
                              ...watch(`manual_metrics.${index}.settings.condition`),
                              range: { parameters: [], type: 'n' },
                            }}
                            onChangeHandler={(filter) =>
                              handleFilterLiveReportRecords(filter, index)
                            }
                            keysForFiltering={getFilteringKeys() || []}
                            modalMode
                            modalModeStyle="second"
                            withoutRange
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-[6px]">
                        <CustomLabel label="Привязать общий файл" />
                        <div>
                          <UploadButton
                            description={`Максимальный размер 10 MB./nФорматы DOCX, DOC, TXT, PDF./nНе поддерживаем PNG.`}
                            loadedFiles={[]}
                            changeLoadedFiles={() => console.log('tt')}
                            descriptionPosition="right"
                            soon
                          />
                        </div>
                      </div>
                    </div>
                    {/* GRID END */}
                  </GridWrapper>
                </IndividualMetricItemWrapper>
              );
            })}
          </div>
        </SimpleAccordion>
        {!newIndividualMetricId && (
          <div className="w-full flex items-center justify-center">
            <Button
              onClick={() => appendManualMetricBlock()}
              label="Добавить метрику"
              icon="PlusIcon"
              fill="linked"
              className="h-fit"
              disabled={status?.project_status === AllStatusProject.ACTIVE}
            />
          </div>
        )}
      </div>
      {/* TEMPLATES MODAL */}
      <EasyModal
        show={!!metricIdToFill}
        onClose={() => setMetricIdToFill(undefined)}
        label={<div className="">{t('from_template_add')}</div>}
        variant="mediumW700"
        withoutFooter
        withoutContentPadding
      >
        <div className="w-full px-[24px] flex flex-col gap-[8px]">
          {metricTemplates
            ?.map((templateGroup) => ({
              ...templateGroup,
              metric_templates: templateGroup?.metric_templates?.filter(
                (template) => template.type === 'gpt',
              ),
            }))
            ?.map((group) => {
              return (
                <SimpleAccordion
                  dataId={`ap-metric-group-${group.name}`}
                  key={group.metric_templates_group_id}
                  id={group.metric_templates_group_id}
                  label={<div className="font-medium text-[15px] text-0color">{group.name}</div>}
                  onChange={clickAccordionHeaderBtn}
                  arrowClassName="!text-0color"
                  opened={activeAccordionItem === group.metric_templates_group_id}
                >
                  {activeAccordionItem === group.metric_templates_group_id && (
                    <ul className="flex w-full rounded-[12px] flex-col  border-[1px] border-basic_app_bg">
                      {group.metric_templates?.map((type) => {
                        return (
                          <li
                            data-id={`ap-metric-group-${group.name}-${type.name}`}
                            className="w-full items-center justify-between flex border-b-[1px] border-basic_app_bg p-[8px] font-medium text-[15px] text-0color h-[64px]"
                            key={type.metric_template_id}
                          >
                            <button
                              onClick={() => handleFillMetric(type)}
                              className={`flex w-full items-center justify-between`}
                            >
                              {type.name}
                              {/* <span className="w-[28px] text-[#1C1C1E] bold">&gt;</span> */}
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 12H19"
                                  stroke="#1F2125"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 5L19 12L12 19"
                                  stroke="#1F2125"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </SimpleAccordion>
              );
            })}
        </div>
      </EasyModal>
      <AddToTemplateModal
        show={!!metricToTemplate}
        name={metricToTemplate?.name || 'N/A'}
        applyBtn={createMetricTemplateApplyBtn}
        onClose={() => setMetricToTemplate(undefined)}
      />
      {/* TEMPLATES MODAL */}
      {/* Transfer Metric modal */}
      <EasyModal
        show={!!metricToTransfer}
        onClose={onCloseTransferMetricModal}
        withoutFooter
        label="Переместить метрику в группу"
      >
        <>
          <div className="grid grid-cols-4 gap-[10px] pb-[20px]">
            {(gptGroupsList as GPTGroupMetricListObjectType).gpt_groups
              .filter((g) => g.gpt_group_id !== gptModalData?.gpt_group_id)
              .map((group) => (
                <div
                  key={group.gpt_group_id}
                  onClick={() => handleChooseGroupToTransfer(group.gpt_group_id)}
                  className="flex items-center justify-center p-2 rounded-defaultR bg-black/[.05] cursor-pointer hover:bg-black/[.1]"
                >
                  <div className="truncate text-[15px] text-1color">{group.name}</div>
                </div>
              ))}
          </div>
          <EasyModal
            variant="medium484"
            show={!!gptGroupIdForTransfer}
            withoutFooter
            onClose={onCloseApproveTransferModal}
            label="Предупреждение"
          >
            <>
              <div className="text-center w-full">
                Вы собираетесь перенести метрику в другую группу если вы это сделаете метрика будет
                удалена из данной группы
              </div>
              <div className="py-4">
                <Button label="Перенести" onClick={handleTransferMetric} />
                <Button label="Отмена" fill="linked" onClick={onCloseApproveTransferModal} />
              </div>
            </>
          </EasyModal>
        </>
      </EasyModal>
      {/* Transfer Metric modal */}
    </>
  );
};

const IndividualMetricItemWrapper = ({
  children,
  isNew,
  label,
  onCancel,
  accordion,
  handleFillMetricFromTemplate,
}: {
  children: ReactNode;
  isNew: boolean;
  label?: string | ReactNode;
  handleFillMetricFromTemplate?(): void;
  onCancel(): void;
  accordion: { accordionOpenedIds: string[]; handleClickAccordion(id: string): void; id: string };
}) => {
  const isOpened = accordion.accordionOpenedIds.includes(accordion.id);
  if (!isNew) {
    return (
      <SimpleAccordion
        opened={isOpened}
        id={accordion.id}
        onChange={accordion.handleClickAccordion}
        label={label}
        summaryClassName={isOpened ? '' : '!mb-0'}
      >
        {children}
      </SimpleAccordion>
    );
  }
  return (
    <div className="">
      <div className="pb-[12px] flex items-center justify-between">
        <span className="font-bold text-[24px] text-0color">
          Создание GPT-индивидуальной метрики
        </span>
        <div className="self-end flex items-center gap-[5px]">
          {/* <CircleButton className="text-action" icon="FolderPlusIcon" size={18.5} /> */}
          <CircleButton
            hintTitle="Загрузить из шаблона"
            className="text-action"
            onClick={handleFillMetricFromTemplate}
            icon="FolderIcon"
            size={17}
          />
          <Button
            onClick={onCancel}
            className="h-fit danger"
            variant="danger"
            fill="linked"
            label="Отменить"
          />
        </div>
      </div>
      {children}
    </div>
  );
};
